import {
  deleteFile,
  getPortfolioFileContentBlob,
  listPeriods,
  listPortfolioFileCatalog,
  uploadFile,
  type CoworkPeriod,
  type CoworkFileCatalog,
  type CoworkPortfolioFile,
} from "@/cowork/api"
import { notifyCoworkFilesUploaded } from "@/cowork/file-events"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { collectionPath, useCollectionScope } from "@/cowork/scope-url"
import { useAppNavigate } from "@/cowork/workspace-route"
import { useLanguage } from "@/context/language"
import { trialErrorKey } from "@/cowork/trial-errors"
import { CoworkFilePreview } from "@/components/cowork-file-preview"
import { FileCatalogFilters, documentTypeLabel, type FileCategory } from "@/components/file-catalog-filters"
import { createEffect, createMemo, createResource, createUniqueId, For, on, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"

type DataSection = "files" | "datasets" | "extracted" | "sources"
type PeriodFolder = {
  id: string
  periodID: string
  companyID: string
  projectID: string
  name: string
  company: string
  project: string
  files: CoworkPortfolioFile[]
  metadata?: CoworkPeriod
}

type PortfolioUpload = {
  id: number
  file: File
  destination: {
    companyID: string
    projectID: string
    periodID: string | null
    label: string
    locked: boolean
  }
  viewScope: {
    entityID: string
    projectID?: string
    periodID?: string
    snapshotFiles?: { id: string; name: string }[]
  }
  status: "pending" | "uploaded" | "error"
  error: "" | "uploadUnconfirmed" | "uploadScopeMismatch" | "locked"
  result?: Awaited<ReturnType<typeof uploadFile>>
  startedAt: string
}

export function CoworkFilesPage() {
  const scope = useCollectionScope()
  const navigate = useAppNavigate()
  return (
    <CoworkFilesView
      entityID={scope().companyID || "ALL"}
      projectID={scope().projectID}
      periodID={scope().periodID}
      navigateFolder={navigate}
    />
  )
}

export function CoworkDataScopeView(props: { entityID: string; projectID?: string; periodID?: string }) {
  const language = useLanguage()
  const [state, setState] = createStore({ section: "files" as DataSection })
  const sections: DataSection[] = ["files", "datasets", "extracted", "sources"]

  return (
    <div>
      <div class="mb-5 flex gap-1 overflow-x-auto rounded-lg bg-v2-background-bg-layer-02 p-1">
        <For each={sections}>
          {(section) => (
            <button
              type="button"
              class="whitespace-nowrap rounded-md px-3 py-2 text-[11px] font-medium text-v2-text-text-muted hover:text-v2-text-text-base"
              classList={{
                "bg-v2-background-bg-base text-v2-text-text-base shadow-[var(--v2-elevation-raised)]":
                  state.section === section,
              }}
              onClick={() => setState("section", section)}
            >
              {language.t(`cowork.enterprise.data.${section}` as keyof typeof import("@/i18n/en").dict)}
            </button>
          )}
        </For>
      </div>
      <Show when={state.section === "files"}>
        <CoworkFilesView entityID={props.entityID} projectID={props.projectID} periodID={props.periodID} embedded />
      </Show>
      <Show when={state.section === "datasets"}>
        <CoworkFilesView
          entityID={props.entityID}
          projectID={props.projectID}
          periodID={props.periodID}
          embedded
          initialCategory="dataset"
        />
      </Show>
      <Show when={state.section === "extracted"}>
        <DataEmpty
          title={language.t("cowork.enterprise.data.extractedEmptyTitle")}
          description={language.t("cowork.enterprise.data.extractedEmptyDescription")}
        />
      </Show>
      <Show when={state.section === "sources"}>
        <DataEmpty
          title={language.t("cowork.enterprise.data.sourcesEmptyTitle")}
          description={language.t("cowork.enterprise.data.sourcesEmptyDescription")}
        />
      </Show>
    </div>
  )
}

export function CoworkFilesView(props: {
  entityID: string
  projectID?: string
  periodID?: string
  embedded?: boolean
  initialCategory?: FileCategory
  snapshotFiles?: readonly { id: string; name: string }[]
  navigateFolder?: (path: string) => void
}) {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const destinationID = createUniqueId()
  const scope = createMemo(
    () =>
      Object.freeze({
        entityID: props.entityID,
        projectID: props.projectID && props.projectID !== "ALL" ? props.projectID : undefined,
        periodID: props.periodID && props.periodID !== "ALL" ? props.periodID : undefined,
        snapshotFiles: props.snapshotFiles?.map((file) => ({ id: file.id, name: file.name })),
      }),
    undefined,
    {
      equals: (previous, next) =>
        previous.entityID === next.entityID &&
        previous.projectID === next.projectID &&
        previous.periodID === next.periodID &&
        (previous.snapshotFiles === next.snapshotFiles ||
          (previous.snapshotFiles !== undefined &&
            next.snapshotFiles !== undefined &&
            previous.snapshotFiles.length === next.snapshotFiles.length &&
            previous.snapshotFiles.every(
              (file, index) =>
                file.id === next.snapshotFiles![index].id && file.name === next.snapshotFiles![index].name,
            ))),
    },
  )
  const scopeKey = createMemo(() => JSON.stringify(scope()))
  const [state, setState] = createStore({
    search: "",
    searchQuery: "",
    sellerIDs: [] as string[],
    documentTypes: [] as string[],
    category: props.initialCategory ?? ("all" as FileCategory),
    folder: undefined as PeriodFolder | undefined,
    preview: undefined as CoworkPortfolioFile | undefined,
    scopeKey: scopeKey(),
    deleting: "",
    error: "",
  })
  const currentFolder = () => (state.scopeKey === scopeKey() ? state.folder : undefined)
  const query = createMemo(
    () => {
      const selected = scope()
      const folder = currentFolder()
      const current = state.scopeKey === scopeKey()
      const options = {
        entityID: folder?.companyID ?? selected.entityID,
        projectID: folder?.projectID ?? selected.projectID,
        periodID: folder?.periodID ?? selected.periodID,
        search: current ? state.searchQuery : "",
        sellerIDs: current ? [...state.sellerIDs] : [],
        documentTypes: current ? [...state.documentTypes] : [],
        category: current ? state.category : (props.initialCategory ?? "all"),
        fileIDs: selected.snapshotFiles?.map((file) => file.id),
      }
      return {
        scope: selected,
        options,
        key: JSON.stringify(options),
        viewKey: JSON.stringify([scopeKey(), folder?.id ?? null]),
      }
    },
    undefined,
    {
      equals: (previous, next) =>
        previous?.scope === next.scope && previous.key === next.key && previous.viewKey === next.viewKey,
    },
  )
  type CatalogResult = CoworkFileCatalog & { scope: ReturnType<typeof scope>; key: string; viewKey: string }
  const [files, fileActions] = createResource<CatalogResult, ReturnType<typeof query>>(
    query,
    async (selected, previous) => {
      const catalog =
        selected.scope.snapshotFiles?.length === 0
          ? { items: [], total: 0, available_file_ids: [], sellers: [], document_types: [], accounting: false }
          : await listPortfolioFileCatalog(selected.options)
      const previousItems = new Map(
        (previous.value?.scope === selected.scope ? previous.value.items : []).map((file) => [file.id, file]),
      )
      return {
        ...catalog,
        scope: selected.scope,
        key: selected.key,
        viewKey: selected.viewKey,
        items: catalog.items.map((file) => {
          const prior = previousItems.get(file.id)
          return prior && JSON.stringify(prior) === JSON.stringify(file) ? prior : file
        }),
      }
    },
  )
  createEffect(
    on(
      () => [scopeKey(), state.search] as const,
      ([key, text]) => {
        const timer = setTimeout(() => {
          if (scopeKey() === key) setState("searchQuery", text.trim())
        }, 250)
        onCleanup(() => clearTimeout(timer))
      },
    ),
  )
  const catalog = createMemo<CatalogResult | undefined>((previous) => {
    const sameView = (value: CatalogResult | undefined) => value?.scope === scope() && value.viewKey === query().viewKey
    if (files.state === "ready" || files.state === "refreshing") {
      const value = files.latest
      return sameView(value) ? value : undefined
    }
    // Keep open filter menus mounted while a new filter query is pending in the same view.
    return files.state === "pending" && sameView(previous) ? previous : undefined
  })
  const periodScope = createMemo(() =>
    props.snapshotFiles === undefined &&
    props.projectID &&
    props.projectID !== "ALL" &&
    props.entityID &&
    props.entityID !== "ALL"
      ? { companyID: props.entityID, projectID: props.projectID }
      : undefined,
  )
  const [periods, periodActions] = createResource(periodScope, async (selected) => ({
    scope: selected,
    items: await listPeriods(selected.projectID, selected.companyID),
  }))
  const periodError = () => !!periodScope() && periods.error
  // Before the first accepted value (or after an error), even .latest can suspend or throw.
  const periodMetadata = createMemo(() => {
    if (periods.state !== "ready" && periods.state !== "refreshing") return []
    const value = periods.latest
    const selected = periodScope()
    if (!selected || value?.scope !== selected) return []
    return value.items.filter(
      (period) => period.company_id === selected.companyID && period.project_id === selected.projectID,
    )
  })
  const companies = createMemo(() =>
    context.companies.state === "ready" || context.companies.state === "refreshing"
      ? context.companies.latest
      : undefined,
  )
  const projects = createMemo(() =>
    context.projects.state === "ready" || context.projects.state === "refreshing" ? context.projects.latest : undefined,
  )
  createEffect(
    on(scope, (selected) =>
      setState({
        scopeKey: JSON.stringify(selected),
        folder: undefined,
        preview: undefined,
        search: "",
        searchQuery: "",
        sellerIDs: [],
        documentTypes: [],
        category: props.initialCategory ?? "all",
        error: "",
      }),
    ),
  )
  const openFolder = (folder?: PeriodFolder) => {
    if (props.snapshotFiles === undefined && props.navigateFolder) {
      const projectID = folder?.projectID ?? scope().projectID
      if (projectID) {
        props.navigateFolder(collectionPath("files", { projectID, periodID: folder?.periodID }))
        return
      }
    }
    setState({
      folder,
      search: "",
      searchQuery: "",
      sellerIDs: [],
      documentTypes: [],
      category: props.initialCategory ?? "all",
    })
  }
  let alive = true
  onCleanup(() => {
    alive = false
  })
  let root: HTMLDivElement | undefined
  const refresh = () => {
    if (
      !alive ||
      files.loading ||
      state.preview ||
      document.visibilityState !== "visible" ||
      !root?.getClientRects().length
    )
      return
    void Promise.resolve(fileActions.refetch()).catch(() => {})
  }
  onMount(() => {
    const timer = window.setInterval(refresh, 15000)
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)
    onCleanup(() => {
      window.clearInterval(timer)
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", refresh)
    })
  })
  const scopedFiles = createMemo(() => {
    if (files.state !== "ready" && files.state !== "refreshing") return []
    const value = files.latest
    const source = value?.scope === scope() && value.key === query().key ? value.items : []
    if (props.snapshotFiles === undefined) return source
    const ids = new Set(props.snapshotFiles.map((file) => file.id))
    return source.filter((file) => ids.has(file.id))
  })
  const unavailableFiles = createMemo(() => {
    if (files.state !== "ready" || files.latest?.scope !== scope()) return []
    const available = new Set(files.latest.available_file_ids)
    return (props.snapshotFiles ?? []).filter((file) => !available.has(file.id))
  })
  const filtered = scopedFiles
  const clearFilters = () =>
    setState({
      search: "",
      searchQuery: "",
      sellerIDs: [],
      documentTypes: [],
      category: props.initialCategory ?? "all",
    })
  const hasFilters = () =>
    !!state.search ||
    state.sellerIDs.length > 0 ||
    state.documentTypes.length > 0 ||
    state.category !== (props.initialCategory ?? "all")
  const periodGroups = createMemo(() => {
    if (props.snapshotFiles !== undefined) return []
    const groups = new Map<string, PeriodFolder>()
    for (const file of scopedFiles()) {
      const periodID = file.period_id ?? file.workflow_id
      if (!periodID) continue
      const key = filePeriodKey(file)
      const group = groups.get(key) ?? {
        id: key,
        periodID,
        companyID: file.company_id,
        projectID: file.project_id,
        name: file.period_name || language.t("cowork.enterprise.files.unnamedPeriod"),
        company: file.company_name,
        project: file.project_name,
        files: [],
      }
      group.files.push(file)
      groups.set(key, group)
    }
    for (const period of periodMetadata()) {
      if (scope().periodID && period.id !== scope().periodID) continue
      const key = JSON.stringify([period.company_id, period.project_id, period.id])
      const existing = groups.get(key)
      groups.set(key, {
        id: key,
        periodID: period.id,
        companyID: period.company_id,
        projectID: period.project_id,
        name: period.name || language.t("cowork.enterprise.files.unnamedPeriod"),
        company:
          existing?.company ||
          companies()?.find((company) => company.id === period.company_id)?.name ||
          period.company_id,
        project:
          existing?.project ||
          projects()?.find((project) => project.id === period.project_id && project.company_id === period.company_id)
            ?.name ||
          period.project_id,
        files: existing?.files ?? [],
        metadata: period,
      })
    }
    return [...groups.values()]
  })
  // Keep the selected folder during refetches, including folders with no files yet.
  const activePeriod = createMemo(() => {
    const selected = currentFolder()
    return selected ? (periodGroups().find((group) => group.id === selected.id) ?? selected) : undefined
  })
  const flatView = () => props.snapshotFiles !== undefined || !!scope().periodID || !!activePeriod()
  const visibleFiles = createMemo(() =>
    activePeriod() ? filtered().filter((file) => filePeriodKey(file) === activePeriod()!.id) : filtered(),
  )
  const looseFiles = createMemo(() => scopedFiles().filter((file) => !(file.period_id ?? file.workflow_id)))
  const destination = createMemo(() => {
    if (props.snapshotFiles !== undefined) return undefined
    const folder = activePeriod()
    const companyID = folder?.companyID ?? periodScope()?.companyID
    const projectID = folder?.projectID ?? periodScope()?.projectID
    if (!companyID || !projectID) return undefined
    const periodID = folder?.periodID ?? scope().periodID ?? null
    const period = folder?.metadata ?? periodMetadata().find((item) => item.id === periodID)
    return Object.freeze({
      companyID,
      projectID,
      periodID,
      label: language.t("cowork.enterprise.files.destination", {
        company: folder?.company || companies()?.find((item) => item.id === companyID)?.name || companyID,
        project:
          folder?.project ||
          projects()?.find((item) => item.id === projectID && item.company_id === companyID)?.name ||
          projectID,
        folder:
          folder?.name ||
          period?.name ||
          (periodID
            ? language.t("cowork.enterprise.files.unnamedPeriod")
            : language.t("cowork.enterprise.files.projectFiles")),
      }),
      locked: !!period && (period.is_locked || period.period_status === "closed" || period.period_status === "locked"),
    })
  })
  const [uploads, setUploads] = createStore<PortfolioUpload[]>([])
  const pendingUploads = new Set<number>()
  let nextUploadID = 0
  let picker: HTMLInputElement | undefined
  let pickerDestination: ReturnType<typeof destination>
  let pickerScope: ReturnType<typeof scope>
  const uploadLocked = (item: PortfolioUpload) =>
    item.destination.locked ||
    periodMetadata().some(
      (period) =>
        period.company_id === item.destination.companyID &&
        period.project_id === item.destination.projectID &&
        period.id === item.destination.periodID &&
        (period.is_locked || period.period_status === "closed" || period.period_status === "locked"),
    )
  const submitUpload = async (item: PortfolioUpload) => {
    if (!alive || pendingUploads.has(item.id) || !uploads.some((entry) => entry.id === item.id)) return
    if (uploadLocked(item)) {
      setUploads((entry) => entry.id === item.id, { status: "error", error: "locked" })
      return
    }
    pendingUploads.add(item.id)
    setUploads((entry) => entry.id === item.id, { status: "pending", error: "" })
    const target = item.destination
    let result: Awaited<ReturnType<typeof uploadFile>> | undefined
    try {
      // No session ID: folder uploads belong to the shared period, root uploads explicitly to the project.
      result = await uploadFile(target.projectID, item.file, target.periodID, undefined, target.companyID)
    } catch (error) {
      const key = trialErrorKey(error)
      if (key && alive) setState("error", language.t(key))
      if (alive) setUploads((entry) => entry.id === item.id, { status: "error", error: "uploadUnconfirmed" })
      return
    } finally {
      pendingUploads.delete(item.id)
    }
    if (
      !result ||
      result.company_id !== target.companyID ||
      result.project_id !== target.projectID ||
      (result.period_id ?? result.workflow_id ?? null) !== target.periodID
    ) {
      if (alive) setUploads((entry) => entry.id === item.id, { status: "error", error: "uploadScopeMismatch" })
      return
    }
    notifyCoworkFilesUploaded({
      companyID: result.company_id,
      projectID: result.project_id,
      periodID: result.period_id ?? result.workflow_id ?? null,
    })
    if (!alive) return
    setUploads((entry) => entry.id === item.id, { status: "uploaded", error: "", result })
    if (
      props.snapshotFiles !== undefined ||
      scope().entityID !== item.viewScope.entityID ||
      scope().projectID !== item.viewScope.projectID ||
      scope().periodID !== item.viewScope.periodID
    )
      return
    // A refresh failure is not an upload failure. Keep the confirmed row until a later refresh includes it.
    try {
      const refreshed = await fileActions.refetch()
      if (alive && refreshed?.items.some((file) => file.id === result.id)) {
        setUploads((items) => items.filter((entry) => entry.id !== item.id))
      }
    } catch {}
  }
  const visibleUploads = createMemo(() => {
    if (!flatView() || props.snapshotFiles !== undefined) return []
    const selected = destination()
    if (!selected) return []
    return uploads.filter(
      (item) =>
        item.destination.companyID === selected.companyID &&
        item.destination.projectID === selected.projectID &&
        item.destination.periodID === selected.periodID,
    )
  })
  createEffect(() => {
    const ids = new Set(scopedFiles().map((file) => file.id))
    const reconciled = uploads.filter((item) => item.status !== "uploaded" || !item.result || !ids.has(item.result.id))
    if (reconciled.length !== uploads.length) setUploads(reconciled)
  })

  const downloadFile = async (file: CoworkPortfolioFile) => {
    const selected = scope()
    setState("error", "")
    try {
      const blob = await getPortfolioFileContentBlob(file.id)
      const url = URL.createObjectURL(blob)
      try {
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = file.filename
        anchor.click()
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch {
      if (alive && scope() === selected) setState("error", language.t("cowork.enterprise.files.downloadError"))
    }
  }

  const viewFile = (file: CoworkPortfolioFile) => setState({ preview: file, error: "" })

  const removeFile = async (file: CoworkPortfolioFile) => {
    if (props.snapshotFiles !== undefined) return
    const selected = scope()
    setState({ deleting: file.id, error: "" })
    try {
      await deleteFile(file.project_id, file.id)
      if (alive && state.preview?.id === file.id) setState("preview", undefined)
      if (alive && scope() === selected) await fileActions.refetch()
    } catch {
      if (alive && scope() === selected) setState("error", language.t("cowork.enterprise.files.deleteError"))
    } finally {
      if (alive) setState("deleting", "")
    }
  }

  return (
    <div
      ref={(element) => (root = element)}
      classList={{ "min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10": !props.embedded }}
    >
      <div classList={{ "mx-auto max-w-[1500px]": !props.embedded }}>
        <Show when={!props.embedded}>
          <header>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.navigation.files")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.enterprise.files.description")}</p>
          </header>
        </Show>

        <div class={props.embedded ? "" : "mt-7"}>
          <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <Show when={!activePeriod()}>
              <h2 class="text-[14px] font-semibold">
                {language.t(flatView() ? "cowork.navigation.files" : "cowork.enterprise.files.folders")}
              </h2>
            </Show>
            <Show when={activePeriod()}>
              {(period) => (
                <nav
                  class="flex min-w-0 items-center gap-2 text-[11px]"
                  aria-label={language.t("cowork.navigation.files")}
                >
                  <button
                    type="button"
                    class="flex items-center gap-1.5 text-v2-text-text-muted hover:text-v2-text-text-base"
                    onClick={() => openFolder()}
                  >
                    <IconV2 name="folder" size="small" />
                    {language.t("cowork.enterprise.files.folders")}
                  </button>
                  <span class="text-v2-text-text-faint">/</span>
                  <span class="truncate font-medium text-v2-text-text-base">{period().name}</span>
                </nav>
              )}
            </Show>
            <Show when={props.snapshotFiles === undefined}>
              <button
                type="button"
                class="rounded-lg bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!destination() || destination()?.locked}
                aria-describedby={destinationID}
                onClick={() => {
                  pickerDestination = destination()
                  pickerScope = scope()
                  picker?.click()
                }}
              >
                {language.t("cowork.enterprise.files.addFiles")}
              </button>
              <input
                ref={(element) => (picker = element)}
                type="file"
                multiple
                class="hidden"
                aria-label={language.t("cowork.enterprise.files.addFiles")}
                onChange={(event) => {
                  const selected = Array.from(event.currentTarget.files ?? [])
                  event.currentTarget.value = ""
                  const target = pickerDestination
                  if (!target || target.locked) return
                  for (const file of selected) {
                    const item: PortfolioUpload = {
                      id: nextUploadID++,
                      file,
                      destination: target,
                      viewScope: pickerScope,
                      status: "pending",
                      error: "",
                      startedAt: new Date().toISOString(),
                    }
                    setUploads(uploads.length, item)
                    void submitUpload(item)
                  }
                }}
              />
            </Show>
          </div>
          <Show when={props.snapshotFiles === undefined}>
            <p id={destinationID} class="mb-3 break-words text-[11px] text-v2-text-text-muted">
              {destination()?.locked
                ? language.t("cowork.enterprise.files.locked")
                : destination()?.label || language.t("cowork.enterprise.files.selectProject")}
            </p>
          </Show>
          <Show when={flatView()}>
            <FileCatalogFilters
              scopeKey={query().viewKey}
              category={state.category}
              onCategory={(category) => setState("category", category)}
              accounting={catalog()?.accounting ?? false}
              sellers={catalog()?.sellers ?? []}
              documentTypes={catalog()?.document_types ?? []}
              selectedSellers={state.sellerIDs}
              selectedTypes={state.documentTypes}
              onSellers={(ids) => setState("sellerIDs", ids)}
              onTypes={(types) => setState("documentTypes", types)}
              onClear={clearFilters}
              canClear={hasFilters()}
              shown={visibleFiles().length + visibleUploads().length}
              total={(catalog()?.total ?? 0) + visibleUploads().length}
            >
              <input
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
                aria-label={language.t("cowork.enterprise.files.filters.search")}
                maxLength={500}
                placeholder={language.t("cowork.enterprise.files.filters.search")}
                value={state.search}
                onInput={(event) => setState("search", event.currentTarget.value)}
              />
            </FileCatalogFilters>
          </Show>

          <section
            aria-busy={files.loading}
            class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base"
          >
            <Show when={state.error || files.error || periodError()}>
              <div
                role="alert"
                class="border-b border-v2-border-border-base bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger"
              >
                {state.error || language.t("cowork.enterprise.files.partialError")}
                <Show when={files.error || periodError()}>
                  <button
                    type="button"
                    class="ml-3 underline"
                    onClick={() => {
                      if (files.error) void Promise.resolve(fileActions.refetch()).catch(() => {})
                      if (periodError()) void Promise.resolve(periodActions.refetch()).catch(() => {})
                    }}
                  >
                    {language.t("cowork.enterprise.workflows.reporting.retry")}
                  </button>
                </Show>
              </div>
            </Show>
            <Show
              when={
                flatView()
                  ? visibleFiles().length > 0 || visibleUploads().length > 0
                  : periodGroups().length > 0 || looseFiles().length > 0
              }
              fallback={
                <Show when={!files.error && !periodError()}>
                  <DataEmpty
                    loading={files.loading || periods.loading}
                    title={language.t(
                      hasFilters() ? "prompt.popover.emptyResults" : "cowork.enterprise.files.emptyTitle",
                    )}
                    description={language.t(
                      hasFilters()
                        ? "cowork.enterprise.files.filters.emptyDescription"
                        : props.snapshotFiles !== undefined
                          ? "cowork.enterprise.files.snapshotEmpty"
                          : "cowork.enterprise.files.emptyDescription",
                    )}
                  />
                </Show>
              }
            >
              <Show
                when={flatView()}
                fallback={
                  <ul>
                    <For each={periodGroups()}>
                      {(group) => (
                        <li class="border-b border-v2-border-border-muted last:border-0">
                          <button
                            type="button"
                            class="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-v2-overlay-simple-overlay-hover"
                            onClick={() => openFolder(group)}
                          >
                            <span class="grid size-8 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-v2-icon-icon-muted">
                              <IconV2 name="folder" size="small" />
                            </span>
                            <span class="min-w-0 flex-1">
                              <span class="block truncate text-[12px] font-semibold">{group.name}</span>
                              <span class="mt-0.5 block truncate text-[10px] text-v2-text-text-muted">
                                {group.company} · {group.project}
                              </span>
                            </span>
                            <span class="shrink-0 rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] tabular-nums">
                              {group.files.length}
                            </span>
                            <IconV2 name="outline-square-arrow" size="small" />
                          </button>
                        </li>
                      )}
                    </For>
                    <For each={looseFiles()}>
                      {(file) => (
                        <li
                          class="flex cursor-pointer items-center gap-3 border-b border-v2-border-border-muted px-5 py-4 last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
                          onClick={() => viewFile(file)}
                        >
                          <FileIcon node={{ path: file.filename, type: "file" }} class="size-8 shrink-0" />
                          <div class="min-w-0 flex-1">
                            <button
                              type="button"
                              class="block max-w-full truncate text-left text-[12px] font-semibold"
                              aria-haspopup="dialog"
                            >
                              {file.filename}
                            </button>
                            <div class="mt-0.5 truncate text-[10px] text-v2-text-text-muted">
                              {file.company_name} · {file.project_name}
                            </div>
                          </div>
                          <div onClick={(event) => event.stopPropagation()}>
                            <PortfolioFileActions
                              deleting={state.deleting === file.id}
                              onView={() => viewFile(file)}
                              onDownload={() => void downloadFile(file)}
                              onDelete={() => void removeFile(file)}
                            />
                          </div>
                        </li>
                      )}
                    </For>
                  </ul>
                }
              >
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[680px] border-collapse text-left">
                    <thead>
                      <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                        <th class="px-5 py-3">{language.t("cowork.enterprise.files.name")}</th>
                        <Show when={catalog()?.accounting}>
                          <th class="px-4 py-3">{language.t("cowork.accounting.extractions.party.seller")}</th>
                        </Show>
                        <th class="px-4 py-3">
                          {language.t(
                            catalog()?.accounting
                              ? "cowork.accounting.extractions.fact.documentType"
                              : "cowork.enterprise.files.type",
                          )}
                        </th>
                        <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                        <th class="px-4 py-3">{language.t("cowork.enterprise.files.updated")}</th>
                        <th class="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      <For each={visibleUploads()}>
                        {(upload) => (
                          <UploadFileRow
                            upload={upload}
                            showSeller={catalog()?.accounting ?? false}
                            locked={uploadLocked(upload)}
                            onRetry={() => void submitUpload(upload)}
                            onRemove={() => setUploads((items) => items.filter((entry) => entry.id !== upload.id))}
                          />
                        )}
                      </For>
                      <For each={visibleFiles()}>
                        {(file) => (
                          <PortfolioFileRow
                            file={file}
                            showSeller={catalog()?.accounting ?? false}
                            deleting={state.deleting === file.id}
                            onView={() => viewFile(file)}
                            onDownload={() => void downloadFile(file)}
                            onDelete={props.snapshotFiles === undefined ? () => void removeFile(file) : undefined}
                          />
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </Show>
          </section>
          <Show when={unavailableFiles().length > 0}>
            <section class="mt-4 rounded-lg border border-v2-border-border-base p-4">
              <h3 class="text-[12px] font-medium">{language.t("cowork.enterprise.files.unavailableSnapshot")}</h3>
              <ul class="mt-2 space-y-1 text-[12px] text-v2-text-text-muted">
                <For each={unavailableFiles()}>{(file) => <li class="break-all">{file.name}</li>}</For>
              </ul>
            </section>
          </Show>
        </div>
      </div>
      <Show when={state.preview} keyed>
        {(file) => (
          <CoworkFilePreview
            file={file}
            onClose={() => {
              setState("preview", undefined)
              refresh()
            }}
            onDownload={() => void downloadFile(file)}
            downloadError={state.error}
          />
        )}
      </Show>
    </div>
  )
}

function PortfolioFileRow(props: {
  file: CoworkPortfolioFile
  showSeller?: boolean
  deleting: boolean
  onView: () => void
  onDownload: () => void
  onDelete?: () => void
}) {
  const language = useLanguage()
  return (
    <tr
      class="cursor-pointer border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
      onClick={props.onView}
    >
      <td class="px-5 py-4">
        <div class="flex items-start gap-2">
          <FileIcon node={{ path: props.file.filename, type: "file" }} class="size-4 shrink-0" />
          <span class="min-w-0">
            <button
              type="button"
              class="block max-w-[280px] truncate text-left text-[12px] font-medium"
              aria-haspopup="dialog"
            >
              {props.file.filename}
            </button>
            <span class="mt-0.5 block text-[10px] text-v2-text-text-muted">{formatBytes(props.file.size)}</span>
          </span>
        </div>
      </td>
      <Show when={props.showSeller}>
        <td class="px-4 py-4 text-[11px]">
          <FileSeller file={props.file} />
        </td>
      </Show>
      <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
        <Show when={props.showSeller || (!!props.file.document_summary?.document_type && props.file.document_summary.document_type !== "unknown")}>
          <span class="block text-v2-text-text-base">
            {documentTypeLabel(props.file.document_summary?.document_type, language.t)}
          </span>
        </Show>
        <span class="uppercase" classList={{ "mt-0.5 block text-[10px]": props.showSeller }}>
          {fileExtension(props.file)}
        </span>
      </td>
      <td class="px-4 py-4">
        <Status status={props.file.status} />
      </td>
      <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">{formatDate(props.file.updated_at)}</td>
      <td class="px-5 py-4 text-right" onClick={(event) => event.stopPropagation()}>
        <PortfolioFileActions
          deleting={props.deleting}
          onView={props.onView}
          onDownload={props.onDownload}
          onDelete={props.onDelete}
        />
      </td>
    </tr>
  )
}

function UploadFileRow(props: {
  upload: PortfolioUpload
  showSeller: boolean
  locked: boolean
  onRetry: () => void
  onRemove: () => void
}) {
  const language = useLanguage()
  const status = () => (props.upload.status === "pending" ? "uploading" : props.upload.status)
  return (
    <tr
      data-upload-state={status()}
      class="border-b border-v2-border-border-muted bg-v2-background-bg-layer-01 last:border-0"
    >
      <td class="px-5 py-4">
        <div class="flex items-start gap-2">
          <FileIcon node={{ path: props.upload.file.name, type: "file" }} class="size-4 shrink-0" />
          <span class="min-w-0">
            <span class="block max-w-[280px] truncate text-[12px] font-medium">{props.upload.file.name}</span>
            <span class="mt-0.5 block text-[10px] text-v2-text-text-muted">{formatBytes(props.upload.file.size)}</span>
          </span>
        </div>
      </td>
      <Show when={props.showSeller}>
        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">—</td>
      </Show>
      <td class="px-4 py-4 text-[11px] uppercase text-v2-text-text-muted">
        {fileNameExtension(props.upload.file.name, props.upload.file.type)}
      </td>
      <td class="px-4 py-4" aria-live="polite">
        <Status
          status={status()}
          title={props.upload.error ? language.t(`cowork.enterprise.files.${props.upload.error}`) : undefined}
        />
      </td>
      <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">{formatDate(props.upload.startedAt)}</td>
      <td class="px-5 py-4 text-right">
        <Show when={props.upload.status === "error"}>
          <div class="flex justify-end gap-3 text-[11px]">
            <button type="button" class="disabled:opacity-50" disabled={props.locked} onClick={props.onRetry}>
              {language.t("cowork.enterprise.workflows.reporting.retry")}
            </button>
            <button type="button" onClick={props.onRemove}>
              {language.t("cowork.enterprise.workflows.reporting.remove")}
            </button>
          </div>
        </Show>
      </td>
    </tr>
  )
}

function FileSeller(props: { file: CoworkPortfolioFile }) {
  const language = useLanguage()
  const summary = () => props.file.document_summary
  return (
    <Show
      when={summary()?.sellers.length}
      fallback={
        <span class="text-v2-text-text-muted">
          {summary()?.extraction_state === "failed"
            ? language.t("cowork.enterprise.files.filters.extractionFailed")
            : !summary() || summary()?.extraction_state === "not_extracted"
              ? language.t("cowork.enterprise.files.filters.notExtracted")
              : ["bank_statement", "payroll"].includes(summary()!.document_type)
                ? "—"
                : language.t("cowork.enterprise.files.filters.unknownSeller")}
        </span>
      }
    >
      <For each={summary()?.sellers}>
        {(seller) => (
          <div class="max-w-52">
            <span class="block truncate" title={seller.name}>
              {seller.name}
            </span>
            <Show when={seller.vat_number}>
              <span class="block text-[10px] text-v2-text-text-muted">{seller.vat_number}</span>
            </Show>
          </div>
        )}
      </For>
    </Show>
  )
}

function PortfolioFileActions(props: {
  deleting: boolean
  onView: () => void
  onDownload: () => void
  onDelete?: () => void
}) {
  const language = useLanguage()
  return (
    <MenuV2 gutter={6} modal={false} placement="bottom-end">
      <MenuV2.Trigger
        as={IconButtonV2}
        size="small"
        variant="ghost-muted"
        icon={<IconV2 name="outline-dots" size="small" />}
        aria-label={language.t("cowork.enterprise.files.actions")}
        title={language.t("cowork.enterprise.files.actions")}
      />
      <MenuV2.Portal>
        <MenuV2.Content>
          <MenuV2.Item onSelect={props.onView}>
            <span class="flex items-center gap-2">
              <IconV2 name="outline-square-arrow" size="small" />
              {language.t("cowork.enterprise.reports.view")}
            </span>
          </MenuV2.Item>
          <MenuV2.Item onSelect={props.onDownload}>
            <span class="flex items-center gap-2">
              <IconV2 name="download" size="small" />
              {language.t("cowork.enterprise.files.download")}
            </span>
          </MenuV2.Item>
          <Show when={props.onDelete}>
            <MenuV2.Item disabled={props.deleting} onSelect={props.onDelete}>
              <span class="flex items-center gap-2 text-v2-state-fg-danger">
                <IconV2 name="trash" size="small" />
                {language.t("common.delete")}
              </span>
            </MenuV2.Item>
          </Show>
        </MenuV2.Content>
      </MenuV2.Portal>
    </MenuV2>
  )
}

function filePeriodKey(file: CoworkPortfolioFile) {
  const periodID = file.period_id ?? file.workflow_id
  return JSON.stringify([file.company_id, file.project_id, periodID ?? null])
}

function DataEmpty(props: { title: string; description: string; loading?: boolean }) {
  return (
    <div class="grid min-h-52 place-items-center p-8 text-center">
      <Show
        when={!props.loading}
        fallback={
          <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
        }
      >
        <div>
          <h3 class="text-[14px] font-semibold">{props.title}</h3>
          <p class="mx-auto mt-1 max-w-md text-[11px] leading-5 text-v2-text-text-muted">{props.description}</p>
        </div>
      </Show>
    </div>
  )
}

function Status(props: { status: string; title?: string }) {
  const language = useLanguage()
  const label = () => {
    if (props.status === "uploading") return language.t("cowork.prompt.upload.uploading")
    if (props.status === "uploaded") return language.t("cowork.prompt.upload.uploaded")
    if (props.status === "error") return language.t("cowork.prompt.upload.failed")
    return props.status.replaceAll("_", " ")
  }
  return (
    <span
      title={props.title}
      class="inline-flex rounded-md px-2 py-1 text-[10px] font-medium capitalize"
      classList={{
        "bg-v2-state-bg-success text-v2-state-fg-success": !["uploading", "error"].includes(props.status),
        "bg-v2-state-bg-warning text-v2-state-fg-warning": props.status === "uploading",
        "bg-v2-state-bg-danger text-v2-state-fg-danger": props.status === "error",
      }}
    >
      {label()}
    </span>
  )
}

function fileExtension(file: CoworkPortfolioFile) {
  return fileNameExtension(file.filename, file.content_type)
}

function fileNameExtension(filename: string, contentType: string) {
  return filename.split(".").pop()?.toLowerCase() || contentType.split("/").pop() || "file"
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : ""
}
